import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import setTheme from 'helpers/layout/setTheme';
import { baseHost, FrontEndSiteUrlToStageMap } from 'constants/redirects';
import configure from 'constants/config';
import addCustomFavicons from 'utils/addCustomFavicons';
import { Fund } from 'hooks/data/fund/useFundBySlug';
import { UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import trpcApi, { ApiResponses } from 'lib/trpc';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';
import { UserType } from 'further-types/user';

type WhiteLabellingContextType = {
  firm: ApiResponses['portalSetup']['whitelabelSettings'] | null;
  fund: any; //TODO: use Fund type once it is added to the API
  updateFund: (fund: any) => void; //TODO: use Fund type once it is added to the API
  bespokeAdviserAmount: number | null;
  updateBespokeAdviserAmount: (amount: number | null) => void;
  pendingAccount: { accountId: string; userType: UserType } | null;
  updatePendingAccount: (
    account: { accountId: string; userType: UserType } | null,
  ) => void;
  isLoading: boolean;
  isSubdomain: boolean;
  status: UseQueryResult['status'];
  linkedFirmIds: {
    eisFirmId?: string;
    lpFirmId?: string;
  };
};

export const WhiteLabellingContext = createContext<WhiteLabellingContextType>({
  firm: null,
  fund: null,
  updateFund: () => {},
  bespokeAdviserAmount: null,
  updateBespokeAdviserAmount: () => {},
  pendingAccount: null,
  updatePendingAccount: () => {},
  isLoading: true,
  isSubdomain: false,
  status: 'pending',
  linkedFirmIds: {},
});

const performAdviserSubdomainRedirect = () => {
  setTheme(false);
  if (window.location.pathname === '/') {
    window.open(
      // @ts-expect-error this should not be undefined in reality
      `${FrontEndSiteUrlToStageMap[configure.STAGE]}/adviser-entry`,
      '_self',
    );
  }
};

const addCookieBanner = () => {
  if (document.getElementById('cookieyes')) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'cookieyes';
  script.src =
    'https://cdn-cookieyes.com/client_data/f503be260df045342a516f16/script.js';
  script.integrity =
    'sha384-UUI1P98OdCcvDt53NjIPfR0Vf67TX/60/6DOd8t2WORDcgTFmjubOSj8XI3gPViG';
  script.crossOrigin = 'anonymous';
  document.body.appendChild(script);
};

const isSubdomainFn = (domain: string) =>
  [configure.STAGE, baseHost, 'localhost', 'www', 'adviser'].includes(domain)
    ? false
    : true;

export const WhiteLabellingProvider = ({ children }) => {
  const [fund, setFund] = useState<Fund | null>(null);
  const [bespokeAdviserAmount, setBespokeAmount] = useState<number | null>(
    null,
  );
  const [pendingAccount, setPendingAccount] = useState<{
    accountId: string;
    userType: UserType;
  } | null>(null);

  const domain = useMemo(() => {
    return window.location.hostname.split('.')[0];
  }, []);

  const isSubdomain = isSubdomainFn(domain);

  const { data, isLoading, status } =
    trpcApi.portalSetup.whitelabelSettings.useQuery(
      { domain },
      {
        staleTime: QUERY_STALE_TIME_MS,
        retry: 1,
        retryDelay: 1000,
        placeholderData: keepPreviousData,
        enabled: !!domain,
      },
    );

  useEffect(() => {
    if (domain?.length) {
      switch (domain) {
        case baseHost:
        case 'localhost':
          setTheme(false);
          break;
        case 'uat':
        case 'www':
          setTheme(false);
          if (configure.STAGE === 'production' || configure.STAGE === 'uat') {
            addCookieBanner();
          }
          break;
        case 'adviser':
          performAdviserSubdomainRedirect();
          break;
        default:
          if (!data && status === 'success') {
            // @ts-expect-error this should not be undefined in reality
            window.open(FrontEndSiteUrlToStageMap[configure.STAGE], '_self');
          } else if (data) {
            setTheme(true, data?.whiteLabelConfig);
            addCustomFavicons(data);
          }
          break;
      }
    }
  }, [data]);

  const contextValue = {
    firm: data ?? null,
    isLoading,
    status,
    isSubdomain,
    fund,
    updateFund: setFund,
    linkedFirmIds: {
      eisFirmId: data?.eisFirmId,
      lpFirmId: data?.lpFirmId,
    },
    bespokeAdviserAmount,
    updateBespokeAdviserAmount: setBespokeAmount,
    pendingAccount,
    updatePendingAccount: setPendingAccount,
  };

  return (
    <WhiteLabellingContext.Provider value={contextValue}>
      {children}
    </WhiteLabellingContext.Provider>
  );
};

export const useWhiteLabellingContext = () => {
  return useContext(WhiteLabellingContext);
};

export default WhiteLabellingProvider;
